import { Injectable } from '@angular/core';
import { Auth, User, signOut, onAuthStateChanged } from '@angular/fire/auth';
import { UserRoleService } from './user-role.service';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _user = new BehaviorSubject<any | null>(null);
  public readonly user$: Observable<any | null> = this._user.asObservable();
  private isInitialized = false;

  constructor(private auth: Auth, private router: Router, private userRoleService: UserRoleService) {
    console.log('AuthService initializing...');

    onAuthStateChanged(this.auth, async (user) => {
      this.isInitialized = true; // Set initialized to true when auth state changes
      console.log(
        'Auth state changed:',
        user ? `${user.email} (${user.uid})` : 'No user'
      );
      if (user && user.email) {
        console.log(
          'Auth service initialized with user:',
          user ? 'authenticated' : 'not authenticated'
        );
        // Fetch user role from Firestore using email and phoneNumber
        const userDoc = await this.userRoleService.getUserRoleByEmailOrPhone(user.email ?? undefined, user.phoneNumber ?? undefined);
        if (userDoc && userDoc.role) {
          this._user.next({ ...user, role: userDoc.role, userId: userDoc.id });
        } else {
          this._user.next({ ...user, role: null });
        }
        const returnUrl = this.router.routerState.snapshot.root.queryParams['returnUrl'] || '/dashboard';
        this.handlePostLoginRedirect(returnUrl);
      } else {
        //considered user as not authenticated or logged out
        console.log('User is not authenticated or logged out');
        this._user.next(null);
      }
    });

    // Force initial check after a short delay to ensure Firebase is ready
    setTimeout(() => {
      if (!this.isInitialized) {
        console.log('Forcing auth initialization check...');
        this.isInitialized = true;
        this._user.next(this.auth.currentUser);
      }
    }, 1000);
  }

  get currentUser(): any | null {
    return this._user.value;
  }

  get isAuthInitialized(): boolean {
    return this.isInitialized;
  }

  get isAuthenticated(): boolean {
    return !!this.currentUser;
  }

  // Wait for auth to be initialized
  waitForAuthInitialization(): Promise<User | null> {
    if (this.isInitialized) {
      return Promise.resolve(this.currentUser);
    }
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(this.auth, (user) => {
        unsubscribe();
        resolve(user);
      });
    });
  }

  async signOut(): Promise<void> {
    try {
      await signOut(this.auth);
      this.router.navigate(['/login']);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  

  // Method to handle post-login redirect for different login methods
  handlePostLoginRedirect(returnUrl?: string): void {
    console.log('Handling post-login redirect to:', returnUrl);
    this.router.navigate([returnUrl]);
  }
}
